import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { useLogger } from "../services/logging";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_WALLET, UPDATE_WALLET, GET_WALLETS } from "../graphql/wallets";
import {
  Wallet,
  CreateWalletInput,
  UpdateWalletInput,
  WalletType,
  WalletStatus,
} from "../types/wallet";

interface WalletFormDialogProps {
  open: boolean;
  onClose: () => void;
  wallet?: Wallet | null;
  onSuccess?: () => void;
}

const WalletFormDialog: React.FC<WalletFormDialogProps> = ({
  open,
  onClose,
  wallet,
  onSuccess,
}) => {
  const logger = useLogger("WalletFormDialog");
  const [formData, setFormData] = useState<CreateWalletInput>({
    name: "",
    type: WalletType.SPOT,
    status: WalletStatus.ACTIVE,
    description: "",
    balance: 0,
    currency: "USD",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createWallet] = useMutation(CREATE_WALLET, {
    refetchQueries: [{ query: GET_WALLETS }],
  });

  const [updateWallet] = useMutation(UPDATE_WALLET, {
    refetchQueries: [{ query: GET_WALLETS }],
  });

  const isEdit = !!wallet;

  useEffect(() => {
    if (wallet) {
      setFormData({
        name: wallet.name,
        type: wallet.type,
        status: wallet.status,
        description: wallet.description || "",
        balance: wallet.balance,
        currency: wallet.currency,
      });
    } else {
      setFormData({
        name: "",
        type: WalletType.SPOT,
        status: WalletStatus.ACTIVE,
        description: "",
        balance: 0,
        currency: "USD",
      });
    }
    setErrors({});
  }, [wallet, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Wallet name is required";
    }

    if (formData.balance < 0) {
      newErrors.balance = "Balance cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEdit && wallet) {
        const updateData: UpdateWalletInput = {
          name: formData.name,
          type: formData.type,
          status: formData.status,
          description: formData.description || undefined,
          balance: formData.balance || 0,
          currency: formData.currency,
        };

        await updateWallet({
          variables: {
            id: wallet.id,
            updateWalletInput: updateData,
          },
        });
      } else {
        await createWallet({
          variables: {
            createWalletInput: formData,
          },
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      logger.error("Error saving wallet", { method: "handleSubmit" }, error);
      setErrors({ submit: "Failed to save wallet. Please try again." });
    }
  };

  const handleInputChange = (field: keyof CreateWalletInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Wallet" : "Create New Wallet"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Wallet Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />

          <FormControl fullWidth>
            <InputLabel>Wallet Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              label="Wallet Type"
            >
              <MenuItem value={WalletType.SPOT}>Spot</MenuItem>
              <MenuItem value={WalletType.FUTURES}>Futures</MenuItem>
              <MenuItem value={WalletType.MARGIN}>Margin</MenuItem>
              <MenuItem value={WalletType.OPTIONS}>Options</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value={WalletStatus.ACTIVE}>Active</MenuItem>
              <MenuItem value={WalletStatus.INACTIVE}>Inactive</MenuItem>
              <MenuItem value={WalletStatus.SUSPENDED}>Suspended</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label="Balance"
            type="number"
            value={formData.balance}
            onChange={(e) =>
              handleInputChange("balance", parseFloat(e.target.value) || 0)
            }
            error={!!errors.balance}
            helperText={errors.balance}
            fullWidth
            inputProps={{ step: 0.01 }}
          />

          <TextField
            label="Currency"
            value={formData.currency}
            onChange={(e) => handleInputChange("currency", e.target.value)}
            fullWidth
          />

          {errors.submit && (
            <Typography color="error" variant="body2">
              {errors.submit}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletFormDialog;
